/**
 * Die Rückfrage eines Hintergrundlaufs — woran die Oberfläche sie erkennt.
 * (Ticket 2660, 27.09.2026)
 *
 * Martin, 26.09., auf der Autobahn: *„gerade eine Meldung, Rückfrage an
 * mich, allerdings, da ich gerade auf der Autobahn bin, kann ich das nicht
 * lesen"*. Gemeint war die Chat-Zeile „Rückfrage der Selbstverbesserung ·
 * Ticket #2503: …" von 16:50 — reiner Text, kein Ton, und zwei Züge später
 * aus dem Blick gescrollt.
 *
 * ⚠️ Erkannt wird NUR, was der Lauf selbst als Rückfrage markiert hat: Er
 *    endet mit der festen Zeile „RÜCKFRAGE: …" (parseCliQuestion,
 *    server/task-finalize.ts), und nur dann gibt der Reaper der Chat-Zeile
 *    einen dieser Titel (server/self-task-reaper.ts). Keine Wortliste, kein
 *    Fragezeichen — ein Satz, der zufällig fragt, bleibt eine Meldung.
 *
 * Die Titel stehen hier, damit Reaper und Oberfläche dieselben lesen.
 */
export const RUECKFRAGE_TITEL = {
  selbst: "Rückfrage der Selbstverbesserung",
  auftrag: "Rückfrage von der CLI",
} as const;

type Art = keyof typeof RUECKFRAGE_TITEL;

/** Wie die Herkunft oben in der Karte heißt — in Worten statt im Kürzel. */
const HERKUNFT: Record<Art, string> = {
  selbst: "Selbstverbesserung",
  auftrag: "Hintergrund-Auftrag",
};

interface MeldungsRueckfrage {
  frage: string;
  /** „Selbstverbesserung · Ticket #2503" */
  herkunft: string;
}

/**
 * Die Frage aus der Chat-Zeile des Reapers — oder `null`, wenn die Zeile
 * keine Rückfrage ist.
 *
 * Die Zeile hat die Form `<Titel>[ · Zusatz]: <Datum>, <Uhrzeit> Uhr` und
 * nach einer Leerzeile die Frage selbst.
 *
 * ⚠️ Ohne Leerzeile keine Rückfrage. Die erste Fassung nahm dann „alles
 *    nach dem Doppelpunkt“ — bei einer leeren Frage stand so die Uhrzeit
 *    oben und wurde vorgelesen (vom eigenen Test gefunden, 27.09.).
 */
export function rueckfrageAusMeldung(text: string): MeldungsRueckfrage | null {
  const t = (text ?? "").trimStart();
  for (const art of Object.keys(RUECKFRAGE_TITEL) as Art[]) {
    const titel = RUECKFRAGE_TITEL[art];
    if (!t.startsWith(titel)) continue;
    const doppelpunkt = t.indexOf(": ", titel.length);
    const absatz = t.indexOf("\n\n", doppelpunkt);
    if (doppelpunkt < 0 || absatz < 0) return null;
    const zusatz = t.slice(titel.length, doppelpunkt).trim();
    const frage = t.slice(absatz + 2).trim();
    if (!frage) return null;
    return { frage, herkunft: zusatz ? `${HERKUNFT[art]} ${zusatz}` : HERKUNFT[art] };
  }
  return null;
}
